import { CircleCheckIcon, ClockIcon, LoaderIcon, TriangleAlertIcon } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Skeleton } from '@/components/core/skeleton';
import type { SimulationScenarioDTO } from '@/gateways/impl/SimulationGateway';
import { assertNever } from '@/shared/helpers';

type Props = {
  scenario?: SimulationScenarioDTO;
  isLoading?: boolean;
};

function renderContent({ scenario, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="grid gap-3 py-8">
        <Skeleton className="mx-auto h-8 w-8 rounded-full" />
        <Skeleton className="mx-auto h-4 w-40" />
        <Skeleton className="mx-auto h-3 w-full max-w-sm" />
        <Skeleton className="mx-auto h-3 w-full max-w-xs" />
      </div>
    );
  }
  if (!scenario) {
    return <div className="text-sm font-medium text-foreground">Scenario not found.</div>;
  }
  switch (scenario.runStatus) {
    case 'PENDING':
      return (
        <>
          <ClockIcon className="size-8" aria-hidden />
          <div className="text-sm font-medium text-foreground">Queued…</div>
          <div className="max-w-sm text-xs">
            The simulation engine will pick this run up shortly.
          </div>
        </>
      );
    case 'RUNNING':
      return (
        <>
          <LoaderIcon className="size-8 animate-spin" aria-hidden />
          <div className="text-sm font-medium text-foreground">Running simulation…</div>
          <div className="max-w-sm text-xs">
            This may take a few minutes. You can come back later — results will appear here once the
            engine finishes.
          </div>
        </>
      );
    case 'FAILED':
      return (
        <>
          <TriangleAlertIcon className="size-8 text-destructive" aria-hidden />
          <div className="text-sm font-medium text-foreground">Run failed</div>
          <div className="max-w-sm text-xs">
            The simulation engine could not complete this run. Check the logs and try a re-run.
          </div>
        </>
      );
    case 'COMPLETED':
      return (
        <>
          <CircleCheckIcon className="size-8 text-state-success-foreground" aria-hidden />
          <div className="text-sm font-medium text-foreground">Results coming soon</div>
          <div className="max-w-sm text-xs">
            The results engine is still being wired up. Once it&apos;s live, KPI cards and a
            per-country breakdown will appear here for completed scenarios.
          </div>
        </>
      );
    default:
      return assertNever(scenario.runStatus);
  }
}

export function ScenarioResultsPlaceholder({ scenario, isLoading }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Results</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
          {renderContent({ scenario, isLoading })}
        </div>
      </CardContent>
    </Card>
  );
}
